import type {
  WebformElementFragment,
  WebformElementMetadataFragment,
  WebformElementStatesFragment,
} from '#graphql-operations'

export type NormalizedWebformItem = {
  name: string
  title?: string | null
  required?: boolean | null
  defaultValue?: string | null
  description?: string | null
  descriptionDisplay?: string | null
  disabled?: boolean | null
  fieldPrefix?: string | null
  fieldSuffix?: string | null
  flex?: number | null
  help?: string | null
  helpDisplay?: string | null
  helpTitle?: string | null
  more?: string | null
  moreTitle?: string | null
  readonly?: boolean | null
  titleDisplay?: string | null
  states?: WebformElementStatesFragment | null
  element: WebformElementFragment & {
    items?: NormalizedWebformItem[]
  }
}

function metadataFields(
  metadata?: WebformElementMetadataFragment | null,
): Omit<NormalizedWebformItem, 'element'> {
  return {
    name: metadata?.key ?? '',
    title: metadata?.title,
    required: metadata?.required,
    defaultValue: metadata?.defaultValue,
    description: metadata?.description,
    descriptionDisplay: metadata?.descriptionDisplay,
    disabled: metadata?.disabled,
    fieldPrefix: metadata?.fieldPrefix,
    fieldSuffix: metadata?.fieldSuffix,
    flex: metadata?.flex,
    help: metadata?.help,
    helpDisplay: metadata?.helpDisplay,
    helpTitle: metadata?.helpTitle,
    more: metadata?.more,
    moreTitle: metadata?.moreTitle,
    states: metadata?.states,
  }
}

function hasChildElements(
  element: WebformElementFragment,
): element is WebformElementFragment & {
  elements: (WebformElementFragment | null)[]
} {
  return 'elements' in element && Array.isArray(element.elements)
}

/**
 * Maps a contrib graphql_webform 3.x WebformElement into the shape
 * expected by the FormKit mappers (metadata flattened, nested elements as items).
 */
export function asWebformItem(
  element: WebformElementFragment,
): NormalizedWebformItem {
  const childElements = hasChildElements(element)
    ? element.elements
        .filter((child): child is WebformElementFragment => child !== null)
        .map(asWebformItem)
    : undefined

  return {
    ...metadataFields(element.metadata),
    element: {
      ...element,
      items: childElements,
    },
  }
}
